import {
  basicLogger,
  initialize,
  type LDContext,
  type LDFlagChangeset,
} from 'launchdarkly-js-client-sdk';
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import { ViteEnv } from '@/env';

import { useAuthenticatedUser } from './AuthenticatedUserProvider';

export const FeatureFlag = {
  FeaturePRList: 'FeaturePRList',
  FeatureAskAnnie: 'FeatureAskAnnie',
  FeaturePRImpactAnalysis: 'FeaturePRImpactAnalysis',
  FeatureSearchCoverage: 'FeatureSearchCoverage',
  FeatureDatadogIntegration: 'FeatureDatadogIntegration',
  FeatureGrafanaIntegration: 'FeatureGrafanaIntegration',
  FeatureGCPIntegration: 'FeatureGCPIntegration',
  FeaturePagerduty: 'FeaturePagerduty',
  FeatureRCADemo: 'FeatureRCADemo',
  FeatureGitlabSaaS: 'FeatureGitlabSaaS',
} as const;
export type FeatureFlag = (typeof FeatureFlag)[keyof typeof FeatureFlag];

type FeatureFlagsMap = Partial<Record<FeatureFlag, boolean>>;

const FeatureFlagsContext = createContext<FeatureFlagsMap | null>(null);

export const FeatureFlagsProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const [featureFlags, setFeatureFlags] = useState<FeatureFlagsMap | null>(
    null,
  );
  const { user } = useAuthenticatedUser();

  useEffect(() => {
    const context: LDContext = {
      kind: 'user',
      key: user.email,
      email: user.email,
    };

    const client = initialize(ViteEnv.VITE_FLAG_CLIENT_ID, context, {
      logger: basicLogger({
        level: 'warn',
      }),
    });

    client.waitForInitialization(5).then(() =>
      setFeatureFlags({
        ...(Object.fromEntries(
          Object.entries(client.allFlags()).map(([flagKey, flagValue]) => [
            flagKey,
            flagValue ?? undefined,
          ]),
        ) as FeatureFlagsMap),
      }),
    );

    client.on('change', (newFlags: LDFlagChangeset) => {
      setFeatureFlags(prevFlags => ({
        ...(Object.fromEntries(
          Object.entries(prevFlags ?? {}).map(([flagKey, flagValue]) => [
            flagKey,
            (newFlags[flagKey]?.current as boolean | undefined) ?? flagValue,
          ]),
        ) as FeatureFlagsMap),
        FeatureGrafanaIntegration:
          (newFlags['FeatureGrafanaIntegration']?.current as
            | boolean
            | undefined) ?? false,
        FeatureRCADemo:
          (newFlags['FeatureRCADemo']?.current as boolean | undefined) ?? false,
        FeatureGitlabSaaS:
          (newFlags['FeatureGitlabSaaS']?.current as boolean | undefined) ??
          false,
      }));
    });

    return () => {
      client.close();
    };
  }, [user]);

  if (featureFlags === null) {
    return <></>;
  }

  return (
    <FeatureFlagsContext.Provider value={featureFlags}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export const useFeatureFlags = (): FeatureFlagsMap => {
  const featureFlags = useContext(FeatureFlagsContext);
  if (featureFlags === null) {
    throw new Error(
      'useFeatureFlags must be used within a FeatureFlagsProvider',
    );
  }

  return featureFlags;
};
